import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import RadioButton from "../../../components/Atomos/Radio/RadioButton";
import EditFirm from "../../../components/Catalogs/FirmContact/EditFirm";
import EditMembership from "../../../components/Catalogs/FirmContact/EditMembership";
import { Tab } from "@headlessui/react";
import SaveOverride from "../../../components/Catalogs/SaveOverride";
import GoBack from "../../../components/Moleculas/GoBack";
import CatalogTitle from "../../../components/Moleculas/Catalog Text/CatalogTitle";

type FirmContactJurisdictionTab = {
  jurisdiction: string;
  jurisdictionName?: string;
  contacts?: unknown[];
};

function jurisdictionTabLabel(tab: FirmContactJurisdictionTab): string {
  return String(tab.jurisdictionName ?? tab.jurisdiction ?? "").trim() || "—";
}

const AddFirmContact: React.FC = () => {
  const { state } = useLocation();
  const [selectedOption, setSelectedOption] = useState(state?.level ? 0 : 1);
  const [buttonClicked, setButtonClicked] = useState("");
  const jurisdictionTabs = useMemo<FirmContactJurisdictionTab[]>(
    () => (Array.isArray(state?.jurisdictions) ? state.jurisdictions : []),
    [state?.jurisdictions],
  );
  const [selectedTab, setSelectedTab] = useState(
    jurisdictionTabs[0]?.jurisdiction ?? "",
  );
  const checkEditType = (index: number) => {
    setSelectedOption(index);
  };
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [errorJurisdiction, setErrorJurisdictions] = useState<string[]>([]);
  const [saveOverrideOpen, setSaveOverrideOpen] = useState(false);
  const [update, setUpdate] = useState(false);

  const selectedTabItem = useMemo(
    () =>
      jurisdictionTabs.find((tab) => tab.jurisdiction === selectedTab) ??
      jurisdictionTabs[0],
    [jurisdictionTabs, selectedTab],
  );

  const handleButtonClick = (buttonName: string) => {
    setButtonClicked(buttonName);
  };

  const handleCloseSaveOverride = () => {
    setSaveOverrideOpen(!saveOverrideOpen);
  };

  useEffect(() => {
    if (errorFields.length > 0 || errorJurisdiction.length > 0) {
      setSaveOverrideOpen(true);
      return;
    }
    setSaveOverrideOpen(false);
  }, [errorFields, errorJurisdiction]);

  useEffect(() => {
    if (!jurisdictionTabs.length) {
      return;
    }
    setSelectedTab((current) =>
      jurisdictionTabs.some((tab) => tab.jurisdiction === current)
        ? current
        : jurisdictionTabs[0].jurisdiction,
    );
  }, [jurisdictionTabs]);

  const handleTabValue = (jurisdiction: string) => {
    setSelectedTab(jurisdiction);
  };

  return (
    <>
      <div>
        <SaveOverride
          fields={[...errorFields, ...errorJurisdiction]}
          handleButtonClick={handleButtonClick}
          open={saveOverrideOpen}
          close={handleCloseSaveOverride}
        />
        <CatalogTitle
          title={`Edit ${state?.firmName} Contacts`}
          className="text-Default"
        >
          <GoBack />
        </CatalogTitle>

        <section className="Contacts Edit">
          <div className="flex h-auto w-full gap-5 flex-col p-10 bg-white">
            <div className="flex flex-col lg:flex-row lg:items-center w-full gap-5 bg-white">
              <span className="text-gray-font not-italic leading-6 font-bold text-[22px]">
                Firm Contact Structure:
              </span>
              <RadioButton
                index={0}
                text={"Contacts defined Firm Wide"}
                selected={selectedOption === 0}
                handleRadioButtonChange={checkEditType}
                disabled={state?.firmMembershipType === "Primary"}
              />
              <RadioButton
                index={1}
                text={"Contacts defined by Membership Locations"}
                selected={selectedOption === 1}
                handleRadioButtonChange={checkEditType}
                disabled={state?.firmMembershipType === "Primary"}
              />
            </div>

            {selectedOption === 0 && (
              <EditFirm
                setErrorFields={setErrorFields}
                buttonClicked={buttonClicked}
                update={update}
                setUpdate={setUpdate}
              />
            )}
            {selectedOption === 1 && (
              <>
                <Tab.Group>
                  <Tab.List className={"grid lg:gap-0 lg:grid-cols-6"}>
                    {jurisdictionTabs.map((tab, index) => (
                      <Tab
                        key={tab.jurisdiction || index}
                        onClick={() => handleTabValue(tab.jurisdiction)}
                        className={` justify-center items-center col-span-1 bg-[#E5E3E4] max-h-[190px] text-gray-font ui-selected:text-neutral p-3 pr-2 ui-selected:bg-accent-Default-dark border-none rounded-none focus:border-none`}
                      >
                        <span className="not-italic text-lg leading-8 font-normal">
                          {jurisdictionTabLabel(tab)}
                        </span>
                      </Tab>
                    ))}
                  </Tab.List>
                </Tab.Group>
                <EditMembership
                  jurisdictionId={selectedTabItem?.jurisdiction ?? selectedTab}
                  jurisdictionName={jurisdictionTabLabel(
                    selectedTabItem ?? { jurisdiction: selectedTab },
                  )}
                  jurisdictionObjs={state?.jurisdictionsObjects}
                  setErrorFields={setErrorFields}
                  setErrorJurisdiction={setErrorJurisdictions}
                  buttonClicked={buttonClicked}
                />
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default AddFirmContact;
